/**
 * "download my data" and "delete everything" — the two data rights, on the
 * settings page (CCPA §1798.110 and §1798.105).
 *
 * src/pages/Privacy.tsx and the Trust Center said, honestly, that self-service
 * export and deletion were "being built". This is the surface that makes them
 * true. Precisely what deletion does here: it RECORDS the request, starts the
 * published 30-day clock, shows what will go and what the law makes us keep,
 * and lets the person cancel. The purge itself is still performed by
 * scripts/ops/delete-account-data.mjs — which is why the copy promises "within
 * 30 days" rather than "now", and means it.
 *
 * Four things the copy is doing deliberately:
 *
 *  · It says what the export COVERS before and after, because a member who is
 *    not the workspace owner gets their own data and not the workspace's, and
 *    someone who exported expecting everything would otherwise conclude the
 *    platform holds less than it does.
 *  · It never dead-ends. An incomplete read is a deliberate refusal on the
 *    server (a partial copy that looks complete is the failure that path is
 *    shaped around), so the message says come back, and gives the reference
 *    to quote.
 *  · It is calm. This is a right, not an incident — no scare copy and no
 *    retention offer. The one place it slows down is showing the deletion
 *    summary before anything is scheduled, which is the opposite of a dark
 *    pattern: nobody should queue the destruction of their own matter files
 *    without first reading which records survive it anyway.
 *  · A failed CANCEL says the request is still live. That is the one message
 *    here where a vague "something went wrong" would leave somebody believing
 *    they were safe when they are not.
 */
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  requestMyDataExport,
  requestMyDeletion,
  cancelMyDeletion,
  type ExportResult,
  type DeletionRequestResult,
  type DeletionCancelResult,
} from '../../services/dataRights/exportClient';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const Note = styled.p`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.6;
  color: var(--brand-text-muted);
  max-width: 42rem;
`;

const Ready = styled.div`
  border: 1px solid #e8e4d9;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  background: #fcfbf7;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const DownloadLink = styled.a`
  display: inline-block;
  margin-top: 0.5rem;
  font-weight: 600;
  color: var(--brand-charcoal);
  border-bottom: 1px solid rgba(198, 167, 94, 0.6);
  text-decoration: none;

  &:hover { border-bottom-color: var(--brand-charcoal); }
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #e8e4d9;
  margin: 0.5rem 0;
`;

const Problem = styled.div`
  font-size: 0.88rem;
  line-height: 1.6;
  color: #8a4b2a;
`;

export interface DataExportCardProps {
  /** Injected so this module never imports firebase/auth — see exportClient. */
  getToken: () => Promise<string | null>;
  /** Test seams. */
  requestExport?: typeof requestMyDataExport;
  requestDeletion?: typeof requestMyDeletion;
  cancelDeletion?: typeof cancelMyDeletion;
}

export const DataExportCard: React.FC<DataExportCardProps> = ({
  getToken,
  requestExport = requestMyDataExport,
  requestDeletion = requestMyDeletion,
  cancelDeletion = cancelMyDeletion,
}) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ExportResult | null>(null);
  // Deletion is a two-step: asking to see what would go, then confirming.
  // Not a dark pattern — the opposite. Nobody should schedule the destruction
  // of their own matter files without first reading which records the law
  // makes us keep anyway.
  const [pending, setPending] = useState<DeletionRequestResult | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [cancelled, setCancelled] = useState<DeletionCancelResult | null>(null);

  const run = useCallback(async () => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await requestExport(getToken));
    } finally {
      setBusy(false);
    }
  }, [getToken, requestExport]);

  return (
    <Wrap>
      <Note>
        a copy of everything we hold about you — your conversations, the files you have
        uploaded, what Prest0n remembers, and your billing history. we build it when you ask
        and give you a link that works for an hour.
      </Note>

      <Row>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="request-data-export"
        >
          {busy ? 'putting it together…' : 'download my data'}
        </button>
      </Row>

      {result?.ok === true && (
        <Ready role="status">
          <div>{result.scope}</div>
          <div>
            {result.counts.documents.toLocaleString()} records and{' '}
            {result.counts.objects.toLocaleString()} files, from{' '}
            {result.counts.locations} places we looked.
            {result.truncated.length > 0 && (
              <>
                {' '}
                {result.truncated.length} of them held more than we put in one file — ask us and
                we will send the rest.
              </>
            )}
          </div>
          <DownloadLink href={result.url} target="_blank" rel="noopener noreferrer">
            download it now
          </DownloadLink>
        </Ready>
      )}

      {result?.ok === false && (
        <Problem role="alert">
          {result.message
            ?? 'something went wrong producing your copy. please try again in a moment.'}
        </Problem>
      )}

      <Divider />

      <Note>
        <strong>delete everything.</strong> we remove your data everywhere we hold it. you get
        30 days to change your mind, and nothing goes until that window closes.
      </Note>

      <Row>
        <button
          type="button"
          onClick={async () => {
            setDeleteBusy(true);
            setCancelled(null);
            try { setPending(await requestDeletion(getToken)); } finally { setDeleteBusy(false); }
          }}
          disabled={deleteBusy}
          data-testid="request-deletion"
        >
          {deleteBusy ? 'working…' : 'delete my data'}
        </button>
      </Row>

      {pending?.ok === true && (
        <Ready role="status" data-testid="deletion-scheduled">
          {'summary' in pending ? (
            <>
              <div>
                <strong>scheduled.</strong> {pending.summary.deletes} places will be emptied
                {pending.summary.anonymises > 0 && (
                  <> and {pending.summary.anonymises} stripped of anything identifying you</>
                )}
                {pending.includesWorkspace && <> — including your workspace, which you own</>}.
              </div>
              {pending.summary.keeps.length > 0 && (
                <>
                  {/* Never a footnote. A summary saying "everything will be
                      deleted" while billing records survive is a worse answer
                      than the truth, and CCPA §1798.105(d) is what makes
                      keeping them lawful rather than a refusal. */}
                  <div style={{ marginTop: '0.6rem' }}>
                    <strong>what we have to keep, and why:</strong>
                  </div>
                  <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                    {pending.summary.keeps.map((k) => (
                      <li key={k.what}>
                        {k.what} — {k.forHowLong}. {k.why}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div style={{ marginTop: '0.6rem' }}>
                nothing is removed before{' '}
                <strong>{new Date(pending.purgeAfter).toLocaleDateString()}</strong>. reference{' '}
                {pending.requestRef}.
              </div>
            </>
          ) : (
            <div>
              you already asked us to delete your data
              {pending.purgeAfter && (
                <> — nothing goes before{' '}
                  <strong>{new Date(pending.purgeAfter).toLocaleDateString()}</strong></>
              )}. reference {pending.requestRef}.
            </div>
          )}
          <Row style={{ marginTop: '0.75rem' }}>
            <button
              type="button"
              onClick={async () => {
                setDeleteBusy(true);
                try {
                  const r = await cancelDeletion(getToken);
                  setCancelled(r);
                  if (r.ok) setPending(null);
                } finally { setDeleteBusy(false); }
              }}
              disabled={deleteBusy}
              data-testid="cancel-deletion"
            >
              changed my mind — cancel it
            </button>
          </Row>
        </Ready>
      )}

      {pending?.ok === false && (
        <Problem role="alert">
          {pending.message ?? 'we could not record your request. nothing has been scheduled.'}
        </Problem>
      )}

      {cancelled?.ok === true && (
        <Ready role="status" data-testid="deletion-cancelled">
          cancelled. nothing will be removed, and your account carries on as normal.
        </Ready>
      )}
      {cancelled?.ok === false && (
        <Problem role="alert">
          {cancelled.message
            ?? 'we could not cancel it, and the request is still scheduled. email '
              + 'support@prest0.ai straight away.'}
        </Problem>
      )}
    </Wrap>
  );
};

export default DataExportCard;
